import { UnityBridge } from "@Context/common/general/context";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, FormControl, IconButton, InputBase, Typography, useTheme } from "@mui/material";
import { useState } from "react";

function getXPathForElement(element: any) {
  const idx: any = (sib: any, name: any) => (sib ? idx(sib.previousElementSibling, name || sib.localName) + (sib.localName === name) : 1);
  const segs: any = (elm: any) =>
    !elm || elm.nodeType !== 1
      ? [""]
      : elm.id && document.getElementById(elm.id) === elm
      ? [`id("${elm.id}")`]
      : [...segs(elm.parentNode), `${elm.localName.toLowerCase()}[${idx(elm)}]`];
  return segs(element).join("/");
}

function getScreenCordinates(obj: any) {
  var p = { x: 0, y: 0 };
  p.x = obj.offsetLeft;
  p.y = obj.offsetTop;
  while (obj.offsetParent) {
    p.x = p.x + obj.offsetParent.offsetLeft;
    p.y = p.y + obj.offsetParent.offsetTop;
    if (obj === document.getElementsByTagName("body")[0]) {
      break;
    } else {
      obj = obj.offsetParent;
    }
  }
  return p;
}

interface FormFieldProps {
  id?: string;
  name: string;
  label?: string;
  type: "text" | "email" | "password" | "date" | "number";
  placeholder?: string;
  required?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  conditionalText?: string;
  disabled?: boolean;
}

const BaseFormField = ({
  onChange,
  onClick,
  id,
  name,
  label,
  type,
  placeholder,
  required = false,
  conditionalText,
  disabled = false,
}: FormFieldProps) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Unity event handlers
  const SendFocusUnityEvent = (event: any) => {
    if (UnityBridge) {
      UnityBridge.dispatchWebEvent({
        type: UnityBridge.OutgoingWebEvents.TextFieldFocused,
        payload: event
          ? {
              x_path: getXPathForElement(event.target),
              screen_position: getScreenCordinates(event.currentTarget),
              element_type: event.target.getAttribute("type"),
            }
          : {},
      });
    }
  };

  const SendBlurUnityEvent = (event: any) => {
    if (UnityBridge) {
      UnityBridge.dispatchWebEvent({
        type: UnityBridge.OutgoingWebEvents.TextFieldBlurred,
        payload: event
          ? {
              x_path: getXPathForElement(event.target),
              screen_position: getScreenCordinates(event.currentTarget),
              element_type: event.target.getAttribute("type"),
            }
          : {},
      });
    }
  };

  const SendControllerHapticsEvent = () => {
    if (UnityBridge) {
      UnityBridge.dispatchWebEvent({
        type: UnityBridge.OutgoingWebEvents.TriggerHaptics,
        payload: {
          frequency: 0.25,
          amplitude: 0.1,
          duration: 0.15,
        },
      });
    }
  };

  const onFocusHandler = (event: any, ...focusArgs: any[]) => {
    SendFocusUnityEvent(event);
    SendControllerHapticsEvent();
  };

  const onBlurHandler = (event: any, ...blurArgs: any[]) => {
    SendBlurUnityEvent(event);
  };

  return (
    <FormControl sx={{ width: "100%" }}>
      <Typography variant="textS600" color="dsBase.v200" sx={{ marginBottom: theme.spacing(1) }}>
        {label}
        {required ? "*" : ""}
      </Typography>
      <Box sx={{ position: "relative" }}>
        <InputBase
          id={id}
          required={required}
          name={name}
          type={showPassword ? "text" : type}
          placeholder={placeholder}
          autoComplete="off"
          fullWidth
          onChange={onChange}
          onClick={onClick}
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          disabled={disabled}
          inputProps={{
            sx: {
              border: "1px",
              borderStyle: "solid",
              borderColor: "transparent",
              backgroundColor: !disabled ? theme.palette.dsSurface.v200 : "black", //TODO_CWP determine disabled color (use opacity)
              borderRadius: "12px",
              color: theme.palette.dsBase.v200,
              padding: theme.spacing(2.25),
              paddingRight: isPassword ? theme.spacing(7) : undefined,
              fontFamily: "Inter",
              fontSize: "16px",
              lineHeight: "22px",
              outline: "none",
              "&:focus": {
                backgroundColor: theme.palette.dsBase.v100,
                borderColor: theme.palette.dsPrimary.v300,
              },
              "&:hover": {
                backgroundColor: theme.palette.dsBase.v100,
                borderColor: theme.palette.dsPrimary.v300,
              },
            },
          }}
        />
        {isPassword && (
          <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            sx={{
              position: "absolute",
              right: theme.spacing(2),
              top: "50%",
              transform: "translateY(-50%)",
            }}
          >
            {showPassword ? <Visibility /> : <VisibilityOff />}
          </IconButton>
        )}
      </Box>
      {conditionalText && (
        <Typography
          id="conditional-text"
          variant="textBase600"
          sx={{
            color: theme.palette.dsDestructive.v400,
            marginTop: theme.spacing(1),
          }}
        >
          {conditionalText}
        </Typography>
      )}
    </FormControl>
  );
};

export default BaseFormField;
